import { t } from "i18next";
import { v4 as uuid } from "uuid";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { useRootStore } from "../../../stores/hooks";
import { AddedEventButton } from "../../AddedEventButton";
import { SmallAddActionButton } from "../../SmallAddActionButton";
import { ActionGroup } from "..";
import * as A from "../../../stores/automation/mergers/actions";
import type {
  ActionCandidate,
  ActionGroupType,
} from "../../../types/candidate";
import type { CurrSection } from "../../../containers/AutomationForm/types";
import { triggersIdsAvailableForMergeRecordsAction } from "../../../stores/automation/utils/inputValidation";
import {
  activateNextSection,
  handleActionChange,
  showActionError,
  showActionFieldMissingError,
  showActionFieldsEmptyError,
} from "../../../utils/action-helpers";
import { BranchesBlockCard } from "./BranchesBlockCard";
import { NestedBranchesFan } from "./NestedBranchesFan";

/* ActionGroup's container carries list-mode geometry (margin: 0 -35px /
   padding: 0 35px / max-width: 420px) that compensates the sidebar's own
   padding; inside a 400px branch column that spills onto the neighbouring
   columns. Re-fit it to the column grid: exactly column-wide, with padding
   that leaves the standard 350px content width for the action cards. A
   nested Conditional Logic block's own fan (Task 7b) is wider than the
   column -- min-width keeps the flat-loop case at its original 400px while
   letting the container grow to fit the fan when one is nested inside. */
const ColumnActionGroup = styled(ActionGroup)<{ $hasNestedBranches?: boolean }>`
  box-sizing: border-box;
  min-width: 400px;
  width: max-content;
  max-width: none;
  margin: 0 0 10px;
  /* longhand only: the container's own padding-bottom (16px, collapse-aware)
     must survive this override */
  padding-left: 25px;
  padding-right: 25px;
  /* With a nested branches block the container grows to the fan's width and the
     block card + fan self-center; center the loop's own action cards + Add
     Action too so the spine lines up (header keeps its own width:100%). Scoped
     to that case so a flat nested loop keeps its left-aligned list. */
  ${({ $hasNestedBranches }) =>
    $hasNestedBranches === true ? "align-items: center;" : ""}
`;

interface NestedLoopGroupProps {
  instanceId: number;
  isActive: boolean;
  /** Stable across renders (the group binds its own instanceId) so the
   * group's React.memo isn't defeated by a fresh closure per render. */
  onSelect: (instanceId: number) => void;
  /** Called AFTER the loop is deleted (immediate or confirmed), so the
   * container can fall back the active section if it lived in this loop. */
  onDeleted: () => void;
  activeAction: string | undefined;
  onClickAction: (hash?: string) => void;
  onClickTrigger: () => void;
  onChangeAction: (
    integrationId: string,
    actionId: string,
    actionGroupType?: ActionGroupType,
  ) => void;
  /** Nested Advanced Logic (Task 7b): the currently active canvas section --
   * used to compute selection state for the nested block/fan this loop may
   * itself contain. */
  activeItem: CurrSection;
  /** Nested Advanced Logic (Task 7b): selects a branch of the nested block's
   * own fan -- forwarded straight into NestedBranchesFan's `onSelect`. */
  onSelectBranch: (instanceId: number, branchHash: string) => void;
  /** Nested Advanced Logic (Task 7b): selects a newly-added (or clicked)
   * nested Conditional Logic block living inside this loop. */
  onSelectNestedBlock: (instanceId: number) => void;
}

export const NestedLoopGroup = observer<NestedLoopGroupProps>(
  ({
    instanceId,
    isActive,
    onSelect,
    onDeleted,
    activeAction,
    onClickAction,
    onClickTrigger,
    onChangeAction,
    activeItem,
    onSelectBranch,
    onSelectNestedBlock,
  }) => {
    const store = useRootStore();
    const candidate = store.automations.candidate;
    const looping = candidate.looping;
    const advanced = candidate.branches.loopAdvancedGroup(instanceId);

    const actions = looping.loopActionsAt(instanceId);
    const defaultName = looping.defaultGroupNameAt(instanceId);
    // Output descriptions load asynchronously on open; until then the source
    // can't be resolved, so hold back the empty/error markers (BUG-21925).
    const isDescriptionsLoaded =
      store.automations.isAllDynamicDescriptionsLoaded();
    const isSourceEmpty = isDescriptionsLoaded && !defaultName.isSourceSet;
    const isSourceError =
      isDescriptionsLoaded && !looping.isConfiguredSourceValidAt(instanceId);
    const name =
      looping.loopNameAt(instanceId) ||
      (defaultName.isSourceSet
        ? defaultName.groupName
        : t("general.repeat-for-each-in"));

    const handleClickAction = (accessError?: boolean, hash?: string): void => {
      if (!accessError) onClickAction(hash);
    };

    const handleDuplicate = (hash: string): void => {
      candidate.setActionsAt({ kind: "loop", instanceId }, [
        A.mergeDuplicateAction(hash),
      ]);
    };

    const handleDeleteAction = (
      e: React.MouseEvent,
      currActionHash: string,
    ): void => {
      e.stopPropagation();
      candidate.setActionsAt({ kind: "loop", instanceId }, [
        A.ejectActionReferences(currActionHash),
        A.ejectAction(currActionHash),
      ]);
      activateNextSection(
        currActionHash,
        looping.loopActionsAt(instanceId),
        onClickTrigger,
        onClickAction,
      );
    };

    const showLoopActionErrors = (action: ActionCandidate): boolean => {
      const isSubmitted = candidate.isActionSubmitted(action.hash);

      if (!isSubmitted && candidate.isNewlyAddedAction(action)) {
        return false;
      }

      return (
        showActionFieldsEmptyError(action, isSubmitted) ||
        showActionFieldMissingError(
          store.automations.isAllDynamicDescriptionsLoaded(),
          candidate.self,
          action,
        )
      );
    };

    const handleDelete = (): void => {
      const performDelete = (): void => {
        if (candidate.branches.deleteNestedLoop(instanceId)) onDeleted();
      };
      if (!candidate.branches.loopHasContent(instanceId)) {
        performDelete();
        return;
      }
      store.modal.openConfirmModal({
        type: "warning",
        title: t("confirm-modal.are-you-sure"),
        description: `${t("branches.delete-loop-confirm")}. ${t(
          "confirm-modal.cannot-undone-action",
        )}.`,
        positiveButtonText: t("branches.yes-delete-loop"),
        negativeButtonText: t("confirm-modal.no-cancel"),
        onClose: () => store.modal.closeConfirmModal(),
        onConfirm: () => {
          performDelete();
          store.modal.closeConfirmModal();
        },
      });
    };

    return (
      <div data-testid={`nested-loop-group-${instanceId}`}>
        <ColumnActionGroup
          title={t("general.looping")}
          name={name}
          isEmpty={isSourceEmpty}
          isError={isSourceError}
          isActive={isActive}
          setIsActive={() => onSelect(instanceId)}
          onDelete={handleDelete}
          $hasNestedBranches={advanced?.config.oneofKind === "branches"}
        >
          {/* Nested Advanced Logic (Task 7a): v1 ships WITHOUT drag-reorder for
              actions inside a nested loop. Root-level Looping and BranchColumn's
              own action lists use react-beautiful-dnd, but Figma doesn't call
              out reordering for this nested surface and the task brief doesn't
              require it -- add a DragDropContext here (mirroring BranchColumn)
              if product later asks for full parity. */}
          {actions.map((action) => (
            <AddedEventButton
              key={action.hash}
              groupType="loop"
              eventType="action"
              isHighlighted={activeAction === action.hash}
              isActive={activeAction === action.hash}
              action={action}
              error={showActionError(
                action,
                candidate.actionsSubmitted,
                triggersIdsAvailableForMergeRecordsAction,
              )}
              fieldMissingError={showLoopActionErrors(action)}
              disabled={
                (action.accessError ?? false) ||
                (action.integrationDisabled ?? false) ||
                (action.actionDisabled ?? false)
              }
              onClick={() => {
                handleClickAction(action.accessError, action.hash);
              }}
              onDuplicate={handleDuplicate}
              onDelete={(e, currActionHash) => {
                handleDeleteAction(e, currActionHash);
              }}
              onChange={(integrationId, actionId) => {
                handleActionChange(
                  store,
                  integrationId,
                  actionId,
                  "loop",
                  onChangeAction,
                  onClickAction,
                );
              }}
            />
          ))}

          {/* Nested Advanced Logic (Task 7b): a loop may itself hold a single
              nested Conditional Logic block -- mirror a branch column's own
              layout order (actions -> block card -> Add Action -> fan). */}
          {advanced?.config.oneofKind === "branches" && (
            <BranchesBlockCard
              instanceId={advanced.instanceId}
              isActive={
                activeItem?.type === "branches" &&
                activeItem.instanceId === advanced.instanceId
              }
              onSelect={onSelectNestedBlock}
              onDeleted={() => onSelect(instanceId)}
            />
          )}

          {/* no fullWidth: matches the root Looping container's compact
              Add Action (SmallAddActionButton default), not the branch
              column's full-width bar */}
          <SmallAddActionButton
            disabled={
              candidate.totalStepCount >=
                store.featureFlags.limits.maxActionsPerAutomation ||
              !candidate.self.trigger.triggerReference?.triggerId
            }
            advancedLogic={{
              looping: false,
              branches: candidate.branches.canAddBlockInLoop(instanceId),
            }}
            onClick={(integrationId, actionId) => {
              if (integrationId === "branches") {
                const id = candidate.branches.addBlockInLoop(instanceId);
                if (id !== undefined) onSelectNestedBlock(id);
                return;
              }
              const hash = uuid();
              candidate.setActionsAt({ kind: "loop", instanceId }, [
                A.mergeEmptyAction(hash, integrationId, actionId),
              ]);
              onClickAction(hash);
            }}
          />

          {advanced?.config.oneofKind === "branches" && (
            <NestedBranchesFan
              instanceId={advanced.instanceId}
              activeItem={activeItem}
              activeAction={activeAction}
              onSelect={onSelectBranch}
              onClickAction={onClickAction}
              onClickTrigger={onClickTrigger}
              onChangeAction={onChangeAction}
              onSelectNestedBlock={onSelectNestedBlock}
              onSelectNestedLoop={onSelect}
            />
          )}
        </ColumnActionGroup>
      </div>
    );
  },
);
NestedLoopGroup.displayName = "NestedLoopGroup";
